/* Money and time formatting.
 *
 * Two rules here are not stylistic — getting either wrong produces output that
 * looks perfectly plausible and is wrong:
 *
 *  1. Amounts on the wire are RANDS, not cents. Paystack's own API speaks cents
 *     and the conversion happens server-side; nothing that reaches this client
 *     needs multiplying or dividing by 100.
 *
 *  2. Appointment dates and times are timezone-NAIVE wall clock, not instants.
 *     Round-tripping them through `new Date(...)` reinterprets them in the
 *     viewer's zone. South Africa is UTC+2 year-round with no DST, so in
 *     Johannesburg the bug is invisible — and then a UTC CI runner or a
 *     contractor on holiday shifts every appointment by hours.
 */

export const SAST = "Africa/Johannesburg";

/** `R1 250,00` — matches the homeowner site's `formatMoney()`
 *  (shoku-user-website/src/features/jobs/format.ts) byte-for-byte, so the same
 *  quote amount reads identically on both customer-facing surfaces. */
export function money(amount: number | null | undefined, currency = "ZAR"): string {
  if (amount == null || Number.isNaN(amount)) return "—";
  // `style: "currency"` throws a RangeError on anything that isn't a
  // well-formed ISO 4217 code; every currency reaching here comes off the
  // wire as a free-text Go string with no ISO validation, so fall back
  // rather than let one bad document blank the route.
  const code = /^[A-Za-z]{3}$/.test(currency ?? "") ? (currency as string) : "ZAR";
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: code,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** A budget range, collapsing to a single figure when both ends match. */
export function budgetRange(
  min: number | undefined,
  max: number | undefined,
  currency = "ZAR",
): string {
  if (!min && !max) return "No budget set";
  if (min && max && min !== max) return `${money(min, currency)} – ${money(max, currency)}`;
  return money(min || max, currency);
}

/** "4.2 km" — matches the homeowner map's rendering (`MapIsland.tsx`) of the
 *  same straight-line, one-decimal distance. `undefined`/`0` render nothing:
 *  0.00 is indistinguishable from "unknown" under the API's `omitempty`, and
 *  no real Shoku job sits at exactly 0km away. */
export function km(distance: number | undefined): string {
  if (!distance) return "";
  return `${distance.toFixed(1)} km`;
}

/* ------------------------------------------------------------------ instants */

/**
 * Whether a timestamp represents a real moment.
 *
 * Go's `omitempty` has NO effect on a struct type, so a `time.Time` field that
 * was never set is still serialised — as `"0001-01-01T00:00:00Z"`. A plain
 * truthiness check therefore passes, and the UI cheerfully renders "1 Jan 1"
 * next to "No messages yet". Several Shoku responses do this, including
 * `ConversationResponse.lastMessageAt`.
 */
export function isRealInstant(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const parsed = Date.parse(iso);
  // Year 1 lands around -62135596800000; anything at or before the Unix epoch
  // is not a timestamp this product can legitimately produce.
  return Number.isFinite(parsed) && parsed > 0;
}

/** True RFC 3339 instants (createdAt, updatedAt, payout timestamps) rendered in SAST. */
export function dateTime(iso: string | null | undefined): string {
  if (!isRealInstant(iso)) return "—";
  const parsed = new Date(iso as string);
  return new Intl.DateTimeFormat("en-ZA", {
    timeZone: SAST,
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(parsed);
}

/** RFC 3339 instants with a timezone label (e.g., "07 Sept 2026, 09:30 GMT+2").
 *  Used for booking times that should be rendered with their timezone to prevent
 *  confusion when the contractor's zone differs from their assumptions.
 *  "en-GB" matches the homeowner site's zoneLabel() (shoku-user-website/src/lib/tz.ts)
 *  byte-for-byte, so the same appointment reads identically on both surfaces. */
export function zonedDateTime(iso: string | null | undefined, timeZone: string = SAST): string {
  if (!isRealInstant(iso)) return "—";
  const parsed = new Date(iso as string);
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone,
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZoneName: "short",
    }).format(parsed);
  } catch {
    // An unparseable zone off the wire (legacy document, direct Firestore
    // edit) must not blank the whole route — fall back to SAST, same
    // precedent as money()'s currency-code guard above.
    return zonedDateTime(iso, SAST);
  }
}

/** An instant's Y-M-D as seen in SAST — the cheapest reliable way to compare
 *  two instants by calendar day, or by year, rather than by elapsed seconds. */
function sastYMD(date: Date): { year: number; month: number; day: number } {
  const [year, month, day] = new Intl.DateTimeFormat("en-CA", {
    timeZone: SAST,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .format(date)
    .split("-")
    .map(Number) as [number, number, number];
  return { year, month, day };
}

function sastDayNumber(date: Date): number {
  const { year, month, day } = sastYMD(date);
  return Math.round(Date.UTC(year, month - 1, day) / 86_400_000);
}

export function dateOnly(iso: string | null | undefined, now: Date = new Date()): string {
  if (!isRealInstant(iso)) return "—";
  const parsed = new Date(iso as string);
  const sameYear = sastYMD(parsed).year === sastYMD(now).year;
  return new Intl.DateTimeFormat("en-ZA", {
    timeZone: SAST,
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(parsed);
}

/** "Just now" / "5 min ago" / "3 h ago" / "Yesterday" / "Mon" / "12 Aug" —
 *  the wording every Shoku surface (both apps, this site) settled on. Falls
 *  back to `dateOnly()` past a week, and for future instants (a scheduled
 *  slot must never read "in 2 hours"). */
export function relative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!isRealInstant(iso)) return "";
  const parsed = new Date(iso as string);

  const deltaSeconds = Math.round((parsed.getTime() - now.getTime()) / 1000);
  if (deltaSeconds > 0) return dateOnly(iso, now);

  const absolute = Math.abs(deltaSeconds);
  if (absolute < 60) return "Just now";
  if (absolute < 3600) return `${Math.round(absolute / 60)} min ago`;

  // Calendar-day diff, not `absolute / 86_400` — a same-day timestamp can sit
  // just under 24h ago, and an instant that crossed midnight only 2h ago is
  // still "Yesterday", not "2 h ago".
  const dayDiff = sastDayNumber(now) - sastDayNumber(parsed);
  if (dayDiff === 0) return `${Math.round(absolute / 3600)} h ago`;
  if (dayDiff === 1) return "Yesterday";
  if (dayDiff <= 6) {
    return new Intl.DateTimeFormat("en-ZA", { timeZone: SAST, weekday: "short" }).format(parsed);
  }
  return dateOnly(iso, now);
}

/* -------------------------------------------------------------- wall clock */

/**
 * Formats a naive "YYYY-MM-DD" (+ optional "HH:MM") for display WITHOUT ever
 * constructing a Date from it. The parts are rendered directly, so the string
 * the contractor sees is byte-for-byte the one the API holds.
 */
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function wallClock(
  scheduledDate: string | null | undefined,
  startTime?: string | null,
  endTime?: string | null,
): string {
  if (!scheduledDate) return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(scheduledDate);
  if (!match) return startTime ? `${scheduledDate} ${startTime}` : scheduledDate;

  const [, year, month, day] = match;
  const monthIndex = Number(month) - 1;
  const monthName = MONTHS[monthIndex] ?? month;
  const datePart = `${Number(day)} ${monthName} ${year}`;

  if (!startTime) return datePart;
  return endTime ? `${datePart}, ${startTime}–${endTime}` : `${datePart}, ${startTime}`;
}

/** Today's date as "YYYY-MM-DD" in SAST — the minimum a date input should accept. */
export function todayInSAST(): string {
  // en-CA renders ISO-ordered dates, which is the cheapest reliable way to get
  // Y-M-D for a named timezone without pulling in a date library.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: SAST,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/** The last bookable day: the preauth hold is taken at acceptance and expires
 *  7 days later, so a slot beyond it would deterministically lose its hold
 *  before the work starts (issue #133). Deliberately one day stricter than the
 *  server's now+7d instant (+6 days here, by calendar day, vs the server's
 *  exact 168h) — the picker never offers what the server would refuse, only
 *  the reverse, which just costs a same-time-next-week slot on day 7 itself. */
export function maxScheduleDate(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: SAST,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(Date.now() + 6 * 86_400_000));
}

/** Validates the "HH:MM" 24-hour shape the availability endpoints require. */
export function isValidTime(value: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

/** Minutes since midnight, for comparing and overlap-checking availability windows. */
export function minutesFromTime(value: string): number {
  const [hours, minutes] = value.split(":");
  return Number(hours) * 60 + Number(minutes);
}

/** "2 h 30 min" from a duration in minutes. */
export function duration(minutes: number | null | undefined): string {
  if (minutes == null || minutes <= 0) return "—";
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest} min`;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

/** Initials for an avatar placeholder, safe on empty and single-word names. */
export function initials(...parts: (string | null | undefined)[]): string {
  const letters = parts
    .filter((part): part is string => Boolean(part && part.trim()))
    .map((part) => part.trim()[0]!.toUpperCase());
  return letters.slice(0, 2).join("") || "?";
}
